import { Navigate } from 'react-router-dom';

import AgencyDashboardV2 from '@/routes/Agency/DashboardV2';
import LoadingState from '@/components/LoadingState';
import NATIVE_FEATURE_FLAG from '@/constants/featureFlags';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import ROUTES from '@/routes/routes';

/**
 * Dashboard V2 behind its kill switch.
 *
 * The same key gates the `/api/admin/dashboard_v2/*` endpoints in graphy
 * (`Dashboard::V2Config`), so with the flag off this page is unreachable AND its
 * API 404s — neither half can be reached without the other.
 *
 * Lives in its own module rather than inline in `routes/index.tsx` so it can be
 * rendered directly by a test. The first version of this gate was inline, and
 * its test re-implemented the same three branches beside it; that test passed
 * while the real gate was broken, because the two were only related by having
 * been written from the same intention. Testing the actual export is the point.
 *
 * Two behaviours worth stating, both of which have already gone wrong once:
 *
 * 1. **Wait for `loading`.** `useFeatureFlag` reports `'off'` while the request
 *    is still in flight, so redirecting on that value bounces every admin off
 *    the page on a cold load even when the flag is ON — which reads as "the
 *    feature is broken". Decide only once the value has resolved.
 *
 * 2. **Redirect to `ROUTES.home`, not `ROUTES.basePath`.** `basePath` is the
 *    EMPTY STRING — it exists to mark index children in the route tree — and
 *    `<Navigate to="">` resolves relative to the current location, so it is a
 *    no-op. The first version used it and left a flagged-off admin sitting on
 *    `/dashboard-v2` looking at a blank content area under the navbar, with no
 *    error and nothing to click. `home` is `'/'`.
 */
const DashboardV2Gate = () => {
  const { variant, loading } = useFeatureFlag(NATIVE_FEATURE_FLAG.DashboardV2);

  if (loading) return <LoadingState />;
  if (variant === 'off') return <Navigate to={ROUTES.home} replace />;

  return <AgencyDashboardV2 />;
};

export default DashboardV2Gate;
